'use client';

import { c as _c } from "react/compiler-runtime";
import { type PaymentTermsOptions, useApi } from '@repo/api';
import { z } from '@repo/schema';
import { type CarrierPaymentTermsAccountPayload, type GetCompanyPaymentTerms, type PaymentTermsFormTrigger, type PaymentTermsFormValue, PaymentTermsFormValueSchema, type PaymentTermsPayload, type PaymentTermsResponse, type PaymentTermTrigger, PaymentTermTriggerSchema } from '@repo/schema/payment-terms';
import { format, parseISO } from '@repo/sdk/date';
import { useSuspenseQuery } from '@repo/tanstack/query';
export const COMPANY_DEFAULT_PAYMENT_TERMS_TRIGGER = 'company_default' satisfies PaymentTermsFormTrigger;
export const DEFAULT_PAYMENT_TERMS_FORM_VALUE: PaymentTermsFormValue = {
  bill_date: undefined,
  days: undefined,
  period: undefined,
  trigger: 'prepayment'
};
const PAYMENT_TERMS_TRIGGERS_REQUIRING_DAYS_AND_PERIOD: PaymentTermTrigger[] = ['when_pod_received', 'when_transport_finished'];
const PAYMENT_TERMS_TRIGGERS_SUPPORTING_DATES: PaymentTermTrigger[] = ['selfbill', 'consolidated_invoice'];
const PAYMENT_TERMS_TRIGGER_LABELS: Record<PaymentTermTrigger, string> = {
  consolidated_invoice: 'Consolidated invoice',
  prepayment: 'Prepayment',
  prepayment_before_collection: 'Prepayment (before collection date)',
  selfbill: 'Selfbill',
  when_pod_received: 'When POD received',
  when_transport_finished: 'When transport finished'
};

/** Whose terms: the customer pays our invoice, we pay the carrier's bill. */
export type PaymentTermsParty = 'carrier' | 'customer';

/** The API's period options as select items in the party's wording. */
export const getPaymentTermPeriodOptions = (periods: PaymentTermsOptions['periods'], party: PaymentTermsParty) => periods.map(period => ({
  label: party === 'carrier' ? period.carrier_label : period.customer_label,
  value: period.value
}));
const LEGACY_PAYMENT_TERMS_TRIGGER_CANONICALIZATION: Record<string, PaymentTermTrigger> = {
  when_pod_confirmed: 'when_pod_received',
  when_pod_uploaded: 'when_pod_received'
};
type PaymentTermsSerializerValue = Omit<PaymentTermsFormValue, 'trigger'> & {
  trigger: string;
};
export const requiresPaymentTermsDaysAndPeriod = (trigger: PaymentTermsFormTrigger) => !isCompanyDefaultPaymentTerms(trigger) && PAYMENT_TERMS_TRIGGERS_REQUIRING_DAYS_AND_PERIOD.includes(trigger);
export const triggerSupportsDates = (trigger: PaymentTermsFormTrigger) => !isCompanyDefaultPaymentTerms(trigger) && PAYMENT_TERMS_TRIGGERS_SUPPORTING_DATES.includes(trigger);
export const isCompanyDefaultPaymentTerms = (trigger: PaymentTermsFormTrigger) => trigger === COMPANY_DEFAULT_PAYMENT_TERMS_TRIGGER;
export const normalizePaymentTermTrigger = (trigger?: null | string): PaymentTermTrigger | undefined => {
  if (!trigger) {
    return undefined;
  }
  const normalized = trigger.toLowerCase().trim().replaceAll(/[\s-]+/g, '_');
  if (normalized in LEGACY_PAYMENT_TERMS_TRIGGER_CANONICALIZATION) {
    return LEGACY_PAYMENT_TERMS_TRIGGER_CANONICALIZATION[normalized];
  }
  const parsedTrigger = PaymentTermTriggerSchema.safeParse(normalized);
  if (parsedTrigger.success) {
    return parsedTrigger.data;
  }
  return undefined;
};
const formatUnknownTriggerLabel = (trigger: string) => trigger.replaceAll(/[_-]+/g, ' ').replaceAll(/\s+/g, ' ').trim().replace(/\bpod\b/gi, 'POD').replace(/\beom\b/gi, 'EOM').replace(/\b\w/g, char => char.toUpperCase());
export const getEquivalentPaymentTermTrigger = ({
  availableTriggers,
  trigger
}: {
  availableTriggers: string[];
  trigger: PaymentTermsFormTrigger;
}) => {
  if (isCompanyDefaultPaymentTerms(trigger)) {
    return trigger;
  }
  if (availableTriggers.includes(trigger)) {
    return trigger;
  }
  return availableTriggers.find(value => normalizePaymentTermTrigger(value) === trigger) ?? trigger;
};
export const useFetchPaymentTermsOptions = () => {
  const $ = _c(2);
  const api = useApi();
  let t0;
  if ($[0] !== api.paymentTerms.options) {
    t0 = api.paymentTerms.options.queryOptions();
    $[0] = api.paymentTerms.options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useSuspenseQuery(t0);
};
const createPaymentTermsFieldSchema = ({
  requireDates
}: {
  requireDates: boolean;
}) => PaymentTermsFormValueSchema.superRefine((value, context) => {
  if (requiresPaymentTermsDaysAndPeriod(value.trigger) && !value.days) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Payment term days are required'
    });
  }
  if (requiresPaymentTermsDaysAndPeriod(value.trigger) && !value.period) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Payment term period is required'
    });
  }
  if (requireDates && triggerSupportsDates(value.trigger) && !value.bill_date) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Bill date is required'
    });
  }
});

// Load payment terms support a bill date for selfbill / consolidated invoice triggers.
export const PaymentTermsFieldSchema = createPaymentTermsFieldSchema({
  requireDates: true
});

// Company-level payment terms have no date column, so the bill date is never required.
export const CompanyPaymentTermsFieldSchema = createPaymentTermsFieldSchema({
  requireDates: false
});
export const getDefaultPaymentTermsFormValue = (paymentTerms?: null | PaymentTermsResponse, {
  preferCompanyDefault = false
}: {
  preferCompanyDefault?: boolean;
} = {}): PaymentTermsFormValue => {
  if (!paymentTerms) {
    if (preferCompanyDefault) {
      return {
        bill_date: undefined,
        days: undefined,
        period: undefined,
        trigger: COMPANY_DEFAULT_PAYMENT_TERMS_TRIGGER
      };
    }
    return {
      ...DEFAULT_PAYMENT_TERMS_FORM_VALUE
    };
  }
  if (preferCompanyDefault && paymentTerms.is_inherited) {
    return {
      bill_date: undefined,
      days: undefined,
      period: undefined,
      trigger: COMPANY_DEFAULT_PAYMENT_TERMS_TRIGGER
    };
  }
  const normalizedTrigger = normalizePaymentTermTrigger(paymentTerms.trigger);
  if (!normalizedTrigger) {
    return {
      ...DEFAULT_PAYMENT_TERMS_FORM_VALUE
    };
  }
  return {
    bill_date: paymentTerms.bill_date ? parseISO(paymentTerms.bill_date) : undefined,
    days: paymentTerms.days ?? undefined,
    period: paymentTerms.period ?? undefined,
    trigger: normalizedTrigger
  };
};
export const createPaymentTermsPayload = (value: PaymentTermsSerializerValue): null | PaymentTermsPayload | undefined => {
  if (value.trigger === COMPANY_DEFAULT_PAYMENT_TERMS_TRIGGER) {
    return null;
  }
  const trigger = normalizePaymentTermTrigger(value.trigger);
  if (!trigger) {
    return undefined;
  }
  const requiresDaysAndPeriod = requiresPaymentTermsDaysAndPeriod(trigger);
  return {
    bill_date: triggerSupportsDates(trigger) && value.bill_date ? format(value.bill_date, 'yyyy-MM-dd') : null,
    days: requiresDaysAndPeriod ? value.days ?? null : null,
    period: requiresDaysAndPeriod ? value.period ?? null : null,
    trigger
  } satisfies PaymentTermsPayload;
};

// Serializer for the carrier payment terms on a single load, sent as the nested
// `carrier_payment_terms` object on PATCH /loads/{id}.
//
// Unlike createPaymentTermsPayload this must NEVER return null: a null payload
// clears the load's own columns and makes the backend resolve the terms from the
// carrier company at read time, so a later edit to that company's default would
// retroactively move loads already set. Load-level carrier terms are frozen at
// save, so every trigger serializes to concrete values and the form offers no
// "Carrier Default" option to select.
export const createLoadCarrierPaymentTermsPayload = (value: PaymentTermsSerializerValue): PaymentTermsPayload | undefined => {
  const trigger = normalizePaymentTermTrigger(value.trigger);
  if (!trigger) {
    return undefined;
  }
  const requiresDaysAndPeriod = requiresPaymentTermsDaysAndPeriod(trigger);
  return {
    bill_date: triggerSupportsDates(trigger) && value.bill_date ? format(value.bill_date, 'yyyy-MM-dd') : null,
    days: requiresDaysAndPeriod ? value.days ?? null : null,
    period: requiresDaysAndPeriod ? value.period ?? null : null,
    trigger
  } satisfies PaymentTermsPayload;
};

// Dedicated serializer for the carrier payment terms on a company account.
// Emits exactly the flat keys accepted by PATCH /companies/{id}/account:
// canonical triggers only, days/period set only when the trigger requires
// them (null otherwise), and the UI-only "Not configured" sentinel mapped to
// three explicit nulls. Never emits customer `payment_terms`, `bill_date`,
// or `title`.
export const createCarrierPaymentTermsAccountPayload = (value: PaymentTermsSerializerValue): CarrierPaymentTermsAccountPayload | undefined => {
  if (value.trigger === COMPANY_DEFAULT_PAYMENT_TERMS_TRIGGER) {
    return {
      carrier_payment_days: null,
      carrier_payment_period: null,
      carrier_payment_trigger: null
    };
  }
  const trigger = normalizePaymentTermTrigger(value.trigger);
  if (!trigger) {
    return undefined;
  }
  const requiresDaysAndPeriod = requiresPaymentTermsDaysAndPeriod(trigger);
  return {
    carrier_payment_days: requiresDaysAndPeriod ? value.days ?? null : null,
    carrier_payment_period: requiresDaysAndPeriod ? value.period ?? null : null,
    carrier_payment_trigger: trigger
  } satisfies CarrierPaymentTermsAccountPayload;
};
type PaymentTermsSummaryValue = {
  bill_date?: null | string;
  title?: null | string;
  trigger?: null | string;
};

// The API's `title` already words the terms for the party; this only covers a value without one.
const formatBasePaymentTermsSummary = (paymentTerms?: null | PaymentTermsSummaryValue) => {
  if (!paymentTerms) {
    return '-';
  }
  if (paymentTerms.title?.trim()) {
    return paymentTerms.title;
  }
  if (!paymentTerms.trigger) {
    return '-';
  }
  const normalizedTrigger = normalizePaymentTermTrigger(paymentTerms.trigger);
  const triggerLabel = normalizedTrigger ? PAYMENT_TERMS_TRIGGER_LABELS[normalizedTrigger] : formatUnknownTriggerLabel(paymentTerms.trigger);
  if (normalizedTrigger && triggerSupportsDates(normalizedTrigger) && paymentTerms.bill_date) {
    return `${triggerLabel} - ${format(parseISO(paymentTerms.bill_date), 'd MMM yyyy')}`;
  }
  return triggerLabel;
};
export const getCompanyDefaultPaymentTermsLabel = (paymentTerms?: GetCompanyPaymentTerms | null) => {
  const summary = formatBasePaymentTermsSummary(paymentTerms);
  if (summary === '-') {
    return summary;
  }
  return `Company Default (${summary})`;
};
export const formatPaymentTermsSummary = (paymentTerms?: null | PaymentTermsResponse, {
  companyPaymentTerms,
  includeCompanyDefault = false
}: {
  companyPaymentTerms?: GetCompanyPaymentTerms | null;
  includeCompanyDefault?: boolean;
} = {}) => {
  if (includeCompanyDefault && (paymentTerms?.is_inherited || !paymentTerms)) {
    return getCompanyDefaultPaymentTermsLabel(companyPaymentTerms);
  }
  return formatBasePaymentTermsSummary(paymentTerms);
};